import {
  Combobox,
  type ComboboxFetchParams,
  type ComboboxOption,
  type ComboboxPage,
} from '@/components/combobox';
import { InitialsAvatar } from '@/components/initials-avatar';
import { queryProjects } from '@/lib/projects';

/** A project option carrying everything callers might need to extract. */
export interface ProjectOption extends ComboboxOption {
  name: string;
  slug: string;
}

/** Server-driven page loader for the project combobox (shared, so it's stable). */
export async function fetchProjectPage({
  search,
  page,
}: ComboboxFetchParams): Promise<ComboboxPage<ProjectOption>> {
  const result = await queryProjects({ search, order: 'name', page, pageSize: 20 });
  return {
    items: result.items.map((project) => ({
      id: project.id,
      label: project.name,
      name: project.name,
      slug: project.slug,
      leading: (
        <InitialsAvatar name={project.name} src={project.heroMedia?.url} className="size-6" />
      ),
    })),
    hasMore: page < result.totalPages,
  };
}

interface ProjectSelectProps {
  value: string;
  onChange: (option: ProjectOption) => void;
  placeholder?: string;
}

/** Searchable, auto-paginating project picker (hands back the full option). */
export function ProjectSelect({
  value,
  onChange,
  placeholder = 'Search projects',
}: ProjectSelectProps) {
  return (
    <Combobox<ProjectOption>
      value={value}
      onChange={onChange}
      fetchPage={fetchProjectPage}
      cacheKey="projects"
      placeholder={placeholder}
      emptyLabel="No projects found."
    />
  );
}
